import { Warning } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { FC, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { OrderDto } from '../../dtos';
import { ApiError, LoadingIndicator, useFailedUpdateSnackbar, useSuccessfulUpdateSnackbar } from '../../Components/CoreLib/library';
import { ConfirmDialog } from '../../Components/Core/ConfirmDialog';
import { useGetOrderByIdQuery, useUpdateOrderMutation } from '../../store/generated/generatedApi';
import { OrderForm } from '../../Components/Forms/OrderForm';
import { getErrorMessage, isConflictError } from '../../util/errorHelper';

export const EditOrderView: FC = () => {
    const { orderId, jobId } = useParams();
    const navigate = useNavigate();
    const { data, isLoading, error, refetch } = useGetOrderByIdQuery({ id: orderId! });
    const [updateOrder, { isSuccess, isError, reset, error: updateError }] = useUpdateOrderMutation();
    // Set when the save was rejected because the order changed after this screen loaded it (RK-8176).
    const [conflictMessage, setConflictMessage] = useState<string>();
    // Bumped to remount the form with the latest copy of the order after a conflict.
    const [formKey, setFormKey] = useState(0);

    useSuccessfulUpdateSnackbar('Order', isSuccess, reset);
    // A conflict gets its own dialog instead of the generic failure snackbar.
    useFailedUpdateSnackbar('order', isError && !isConflictError(updateError), reset, updateError);

    const handleSave = async (values: OrderDto) => {
        try {
            return await updateOrder(values).unwrap();
        } catch (saveError) {
            if (isConflictError(saveError)) {
                setConflictMessage(getErrorMessage(saveError as any));
            }
            throw saveError;
        }
    };

    const handleCancel = () => {
        navigate(`/job/${jobId}`);
    };

    const handleCloseConflict = useCallback(() => {
        setConflictMessage(undefined);
        reset();
    }, [reset]);

    const handleReload = useCallback(async () => {
        handleCloseConflict();
        const latest = await refetch();
        // Only remount on a fresh copy; remounting on the old one would just conflict again on the next save.
        if (!latest.isError) {
            setFormKey((key) => key + 1);
        }
    }, [handleCloseConflict, refetch]);

    if (isLoading) {
        return <LoadingIndicator />;
    }

    if (error) {
        return <ApiError onReloadClick={refetch} />;
    }

    return (
        <>
            <OrderForm key={formKey} save={handleSave} cancel={handleCancel} initValues={data} jobId={jobId ?? ''} />
            <ConfirmDialog
                open={!!conflictMessage}
                onClose={handleCloseConflict}
                onConfirm={handleReload}
                title='This order has changed'
                titleIcon={<Warning />}
                content={
                    <>
                        <Typography>{conflictMessage}</Typography>
                        <Typography mt={2}>Reloading discards the changes on this screen.</Typography>
                    </>
                }
                confirmText='Reload Order'
                cancelText='Keep Editing'
                requireConfirmation={false}
            />
        </>
    );
};
